import { authorize } from "@athena-portal/components";
import {FerryPaymentHistoryRecord, FerryPaymentHistoryResponse} from "@ferry/journey-services";
import { getAmountWithDecimalDigits, PluginTemplates } from "@ferry/plugin-ferry-journey";
import * as moment from "moment";
import * as React from "react";
import {getBrandFromPolicyNumber, resourcesHelper} from "@ferry/portal-core";

interface IProps {
    title?: string;
    paymentHistory: FerryPaymentHistoryResponse;
    policyNumber: string;
}

interface IState {
    active: boolean;
    direction: string;
    sortedPayments: FerryPaymentHistoryRecord[];
}

@authorize
export class PaymentHistoryTable extends React.Component<IProps, IState> {

    constructor(props: any) {
        super(props);

        this.state = {
            active: false,
            direction: "asc",
            sortedPayments: this.props.paymentHistory.records
        };

        this.sortByDate = this.sortByDate.bind(this);
    }

    public render(): React.ReactNode {
        return (
            <div className="payment-history-section">
                <div className="d-flex flex-row">
                    <div className="d-none d-md-flex flex-column">
                        <img className="icon-panel mb-0"
                             src={`/${resourcesHelper.THEME_PATH}/img/payment-history.svg`}
                             alt="Payment history icon" aria-label="Payment history icon"
                        />
                    </div>
                    <div className="d-flex flex-column justify-c-center w-100">
                        <div className="d-flex flex-column">
                            <h2 className="font-family-display font-weight-bold d-md-block mb-4">
                                {this.props.title ? this.props.title : "Payment history"}
                            </h2>
                            {this.renderTable()}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    private renderTable(): React.ReactNode {
        if (this.props.paymentHistory && this.props.paymentHistory.records?.length > 0) {
            return (
                <div className="payment-details-section">
                    <div className="payment-history grid-col-2 mt-3 margin-top-0" id={this.props.title?.toLowerCase().split(" ").join("_") ?? "payment_history"}>
                        <table className="two-column-table-header">
                            {this.renderTableHeader()}
                        </table>
                        <div className="table-scroll mt-2">
                            <table className="two-column-table-data">
                                {this.renderTableData()}
                            </table>
                        </div>
                    </div>
                    <div className="payment-history-total-amount grid-col-2">
                        <div className="ph-first-total-amount">
                            Total
                        </div>
                        <div className="ph-second-total-amount">
                            {getAmountWithDecimalDigits(this.props.paymentHistory.totalAmount)}
                        </div>
                    </div>
                </div>
            )
        } else {
            return <p className="mb-0">You don't have payment history yet!</p>;
        }
    }

    private renderTableHeader(): React.ReactNode {
        let cssClass: string;

        if (this.state.active) {
            cssClass = this.state.direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
        } else {
            cssClass = "fas fa-sort";
        }

        return (
            <tr>
                <th>
                    <span className="muli-semi-bold-16">Date</span>
                    <span onClick={this.sortByDate}
                          className={`${cssClass} table-sort-arrow`}
                          role="button"
                          tabIndex={0}
                          aria-label="Sort by date"
                          onKeyDown={(e) => e.key === "Enter" && this.sortByDate()}
                    />
                </th>
                <th className="muli-semi-bold-16">Paid</th>
            </tr>
        )
    }

    private renderTableData(): React.ReactNode {
        return this.state.sortedPayments
            .filter(payment => !payment.failed)
            .map((payment: FerryPaymentHistoryRecord) => {
            const chargebackPayment: boolean = payment.chargebackDate != null;
            const paymentAmountSuffix: string = payment.contributedAmount > 0 ? "*" : "";

            return (
                <tr key={crypto.randomUUID()}>
                    <td className="muli-semi-bold-16 payment-date">
                        <span className="font-weight-light fs-14">{PluginTemplates.convertDate(payment.date, "DD MMMM YYYY")}</span>
                        <br />
                        {chargebackPayment &&
                            <span className="text-danger fs-14">(Chargeback on {moment(payment.chargebackDate).format("DD.MM.YYYY")})</span>
                        }
                        <div className="payment-description">
                            {!chargebackPayment &&
                                <span className="font-weight-regular">{payment.processDescription}</span>
                            }
                        </div>
                        <div>
                            {this.props.policyNumber}
                        </div>
                        {this.renderContributionDescription(payment)}
                    </td>
                    <td className="font-weight-semi-bold payment-amount">
                        <span className={chargebackPayment ? "text-muted text-decoration-line-through" : ""}>
                            {getAmountWithDecimalDigits(payment.paidAmount) + paymentAmountSuffix}
                            {payment.pending && <span className="d-block pending-payment">Pending</span>}
                            {payment.inFlight && <span className="d-block in-flight-payment">In-flight</span>}
                        </span>
                        <br />
                        {chargebackPayment && <span>{getAmountWithDecimalDigits(0)}</span>}
                    </td>
                </tr>
            )
        });
    }

    private renderContributionDescription(payment: FerryPaymentHistoryRecord): React.ReactNode {
        const brand: string | undefined = getBrandFromPolicyNumber(this.props.policyNumber);
        if (payment.depositPayment && payment.totalPayablePremium > 0) {
            return (
                <div className="payment-description">
                    <div className="payment-description">* The price includes a {brand} insurance voucher as per below calculation:</div>
                    <div className="payment-description">Your total annual price is: <b>{getAmountWithDecimalDigits(payment.totalAnnualPremium)}</b></div>
                    <div className="payment-description">Insurance voucher: <b>-{getAmountWithDecimalDigits(payment.contributedAmount)}</b></div>
                    <div className="payment-description">Your price to pay: <b>{getAmountWithDecimalDigits(payment.totalPayablePremium)}</b></div>
                    <div className="payment-description">
                        First payment / deposit, 15% of the annual price ({getAmountWithDecimalDigits(payment.totalPayablePremium)}): <b>{getAmountWithDecimalDigits(payment.paidAmount)}</b>
                    </div>
                </div>
            )
        } else if (payment.contributedAmount > 0) {
            return (
                <div className="payment-description">
                    <div className="payment-description">* The price includes a {brand} insurance voucher as per below calculation:</div>
                    <div className="payment-description">Your total annual price is: <b>{getAmountWithDecimalDigits(payment.amount)}</b></div>
                    <div className="payment-description">Insurance voucher: <b>-{getAmountWithDecimalDigits(payment.contributedAmount)}</b></div>
                    <div className="payment-description">You paid: <b>{getAmountWithDecimalDigits(payment.paidAmount)}</b></div>
                </div>
            )
        } else {
            return <></>
        }
    }

    private sortByDate(): void {
        const sortedPayments: FerryPaymentHistoryRecord[] = this.state.direction === "asc" ?
            [...this.state.sortedPayments].sort((a, b) => moment(a.date).diff(b.date)) :
            [...this.state.sortedPayments].sort((a, b) => moment(b.date).diff(a.date));

        this.setState(prev => ({
            ...prev,
            sortedPayments,
            direction: prev.direction === 'asc' ? 'desc' : 'asc',
            active: true
        }));
    }
}
